import { useProctoredAttempt } from '../components/focus-guard/FocusGuardContext';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { ScenarioBrief } from '../components/eeoc/ScenarioBrief';
import { ScenarioStepper } from '../components/eeoc/ScenarioStepper';
import { ConversationPanel } from '../components/eeoc/ConversationPanel';
import { VoiceAnswerBar } from '../components/eeoc/VoiceAnswerBar';
import { ScenarioResultCard } from '../components/eeoc/ScenarioResultCard';
import { SessionReport } from '../components/eeoc/SessionReport';
import { GradingProgress } from '../components/eeoc/GradingProgress';

const POLL_MS = 3000;

const useGradingPoll = (session, api, sessionId, onDone) => {
    const current = session?.status === 'active' ? session.scenarios[session.current_index] : null;
    const grading = current?.status === 'grading' && !current.grading_failed;
    useEffect(() => {
        if (!grading) return undefined;
        const timer = setInterval(() => {
            api.get(`/eeoc-training/sessions/${sessionId}/grading-status`)
                .then(({ data }) => { if (data.status !== 'grading') onDone(); })
                .catch(() => {});
        }, POLL_MS);
        return () => clearInterval(timer);
    }, [grading, api, sessionId, onDone]);
};

const LiveSession = ({ session, api, onAudio, busyText, autoPlayIndex, onRetryGrading, onContinue, continuing }) => {
    const idx = session.current_index;
    const sc = session.scenarios[idx];
    const recruiterTurns = sc.turns.filter((t) => t.role === 'recruiter').length;
    const isLast = idx === session.scenarios.length - 1;
    const candidateName = sc.scenario.persona.split(',')[0];

    return (
        <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
            <aside className="space-y-4">
                <ScenarioStepper scenarios={session.scenarios} currentIndex={idx} passingScore={session.passing_score_preview} />
                <ScenarioBrief scenario={sc.scenario} />
            </aside>
            <div className="space-y-4">
                <Card>
                    <CardContent className="p-5">
                        <ConversationPanel turns={sc.turns} candidateName={candidateName} api={api} autoPlayIndex={autoPlayIndex} />
                    </CardContent>
                </Card>
                {sc.status === 'active' && (
                    <VoiceAnswerBar
                        onAudio={onAudio}
                        busyText={busyText}
                        disabled={session.read_only}
                        turnLabel={`Your reply ${Math.min(recruiterTurns + 1, session.turns_per_scenario)} of ${session.turns_per_scenario}`}
                    />
                )}
                {sc.status === 'grading' && (
                    <GradingProgress key={`${idx}-${sc.grading_started_at}`} failed={sc.grading_failed} onRetry={onRetryGrading} busy={Boolean(busyText)} />
                )}
                {sc.status === 'graded' && (
                    <ScenarioResultCard evaluation={sc.evaluation} isLast={isLast} onContinue={onContinue} loading={continuing} />
                )}
            </div>
        </div>
    );
};

const EEOCSession = () => {
    const { sessionId } = useParams();
    const assignedTask = useSearchParams()[0].get('qt');
    const { api } = useAuth();
    const navigate = useNavigate();
    const [session, setSession] = useState(null);
    useProctoredAttempt({ active: session?.status === 'active' && !session.read_only, label: 'EEOC module' });
    const [error, setError] = useState(null);
    const [busyText, setBusyText] = useState('');
    const [autoPlayIndex, setAutoPlayIndex] = useState(null);
    const [continuing, setContinuing] = useState(false);
    const [passingScore, setPassingScore] = useState(80);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/eeoc-training/sessions/${sessionId}`);
            setSession(data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Session not found');
        }
    }, [api, sessionId]);

    useEffect(() => {
        load();
        api.get('/eeoc-training/settings').then(({ data }) => setPassingScore(data.min_passing_score)).catch(() => {});
    }, [load, api]);

    useGradingPoll(session, api, sessionId, load);

    const sendAudio = async (blob) => {
        setBusyText('Listening back to your answer...');
        const form = new FormData();
        form.append('audio', blob, 'answer.webm');
        const t = setTimeout(() => setBusyText('The candidate is replying...'), 3500);
        try {
            await api.post(`/eeoc-training/sessions/${sessionId}/respond`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
            clearTimeout(t);
            const sc = session.scenarios[session.current_index];
            setAutoPlayIndex(sc.turns.length + 1);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not send your answer. Please try again.');
        } finally {
            clearTimeout(t);
            setBusyText('');
        }
    };

    const retryGrading = async () => {
        setBusyText('Grading...');
        try {
            await api.post(`/eeoc-training/sessions/${sessionId}/grade`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Evaluation failed. Please try again.');
        } finally {
            setBusyText('');
        }
    };

    const continueSession = async () => {
        setContinuing(true);
        try {
            const isLast = session.current_index === session.scenarios.length - 1;
            await api.post(`/eeoc-training/sessions/${sessionId}/${isLast ? 'complete' : 'next'}`);
            setAutoPlayIndex(0);
            await load();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
        } finally {
            setContinuing(false);
        }
    };

    const retake = async () => {
        setContinuing(true);
        try {
            const { data } = await api.post('/eeoc-training/sessions', { module_id: session.module_id, restart: true });
            setSession(null);
            navigate(`/eeoc-training/session/${data.id}${assignedTask ? `?qt=${assignedTask}` : ''}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not start a new attempt');
        } finally {
            setContinuing(false);
        }
    };

    return (
        <MainLayout>
            <div className="space-y-6 p-6" data-testid="eeoc-session-page">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    {assignedTask ? <span /> : (
                        <button onClick={() => navigate(session?.read_only ? -1 : '/eeoc-training')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="eeoc-session-back">
                            <ArrowLeft className="h-4 w-4" /> {session?.read_only ? 'Back' : 'EEOC Training'}
                        </button>
                    )}
                    {session && (
                        <p className="text-sm text-muted-foreground" data-testid="eeoc-session-module">
                            {session.module.title} · {session.module.law}{session.read_only && session.user_name ? ` · ${session.user_name}` : ''}
                        </p>
                    )}
                </div>
                {error ? (
                    <Card className="border-dashed"><CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="eeoc-session-error">{error}</CardContent></Card>
                ) : !session ? (
                    <div className="grid gap-6 lg:grid-cols-[320px_1fr]"><Skeleton className="h-96" /><Skeleton className="h-96" /></div>
                ) : session.status === 'completed' ? (
                    <SessionReport session={session} onRetake={retake} onBack={() => navigate('/eeoc-training')} retaking={continuing} />
                ) : session.status === 'active' ? (
                    <LiveSession
                        session={{ ...session, passing_score_preview: passingScore }}
                        api={api}
                        onAudio={sendAudio}
                        busyText={busyText}
                        autoPlayIndex={autoPlayIndex}
                        onRetryGrading={retryGrading}
                        onContinue={continueSession}
                        continuing={continuing}
                    />
                ) : (
                    <Card className="border-dashed"><CardContent className="py-12 text-center text-sm text-muted-foreground">This session was replaced by a newer attempt.</CardContent></Card>
                )}
            </div>
        </MainLayout>
    );
};

export default EEOCSession;
